import { useRef, useMemo, useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import type { EventContentArg } from '@fullcalendar/core';
import { convertToCalendarEvents } from '../utils/eventHelpers';
import type { TimetableEntry } from '../types/timetable';
import EventTooltip from './EventTooltip';
import LocationSelector from './LocationSelector';
import TeacherSelector from './TeacherSelector';
import RoomSelector from './RoomSelector';

interface CalendarProps {
  isDarkMode: boolean;
  selectedLocation?: string;
  selectedTeacher?: string;
  selectedRoom?: string;
  onRoomChange?: (room: string) => void;
  showSunday?: boolean;
  timetableData: TimetableEntry[];
}

const Calendar: React.FC<CalendarProps> & { LocationSelector: React.FC<any>; TeacherSelector: React.FC<any>; RoomSelector: React.FC<any> } = ({ isDarkMode, selectedLocation = 'all', selectedTeacher = 'all', selectedRoom = 'all', onRoomChange, showSunday = false, timetableData }) => {
  const calendarRef = useRef<FullCalendar>(null);
  const [calendarKey, setCalendarKey] = useState(0);

  useEffect(() => {
    setCalendarKey(prevKey => prevKey + 1);
  }, [showSunday]);
  
  
  // Filter events based on selected location, teacher, and room
  const filteredEvents = useMemo(() => {
    let filteredData = timetableData;
    
    if (selectedLocation !== 'all') {
      filteredData = filteredData.filter(entry => entry.location === selectedLocation);
    }
    
    if (selectedTeacher !== 'all') {
      filteredData = filteredData.filter(entry => entry.teacher === selectedTeacher);
    }
    
    if (selectedRoom !== 'all' && selectedLocation !== 'all') {
      if (selectedRoom === 'unassigned') {
        filteredData = filteredData.filter(entry => !entry.room || entry.room === '' || entry.roomId === 0);
      } else {
        filteredData = filteredData.filter(entry => entry.room === selectedRoom);
      }
    }
    
    return convertToCalendarEvents(filteredData, isDarkMode);
  }, [timetableData, selectedLocation, selectedTeacher, selectedRoom, isDarkMode]);


  // Custom event content for week view (vertical text with tooltip)
  const renderWeekEventContent = (eventInfo: EventContentArg) => {
    const props = eventInfo.event.extendedProps;
    // Format time manually to ensure correct HH:MM format
    const formatTime = (date: Date | null) => {
      if (!date) return '';
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };
    
    const startTime = formatTime(eventInfo.event.start);
    const endTime = formatTime(eventInfo.event.end);
    
    return (
      <EventTooltip
        isDarkMode={isDarkMode}
        content={{
          title: eventInfo.event.title,
          time: `${startTime} - ${endTime}`,
          course: props.course,
          teacher: props.teacher,
          location: props.location,
          room: props.room,
        }}
      >
        <div
          style={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          {eventInfo.event.title}
        </div>
      </EventTooltip>
    );
  };


  // Uzbek day names
  const dayHeaderContent = (args: any) => {
    const dayNames = ['Yak', 'Dush', 'Sesh', 'Chor', 'Pay', 'Jum', 'Shan'];
    return dayNames[args.date.getDay()];
  };


  return (
    <div style={{ padding: '20px' }}>
      {selectedLocation !== 'all' && onRoomChange && (
        <Calendar.RoomSelector
          selectedRoom={selectedRoom}
          onRoomChange={onRoomChange}
          timetableData={timetableData}
          selectedLocation={selectedLocation}
        />
      )}
      <FullCalendar
        key={calendarKey}
        ref={calendarRef}
        plugins={[timeGridPlugin]}
        initialView="timeGridWeek"
        headerToolbar={false}
        events={filteredEvents}
        height="90vh"
        firstDay={1} // Start week on Monday
        slotMinTime="08:00:00"
        slotMaxTime="21:00:00"
        slotDuration="00:30:00"
        slotLabelInterval="01:00:00"
        slotLabelFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }}
        allDaySlot={false}
        weekends={true}
        hiddenDays={showSunday ? [] : [0]}
        nowIndicator={true}
        dayHeaderContent={dayHeaderContent}
        eventContent={renderWeekEventContent}
        datesSet={(dateInfo) => {
          // For week view, always show current week
          if (dateInfo.view.type === 'timeGridWeek') {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
              const today = new Date();
              const currentWeekStart = dateInfo.view.currentStart;
              
              // Calculate Monday of the current week. If today is Sunday (0), go back 6 days
              const todayWeekStart = new Date(today);
              const day = todayWeekStart.getDay();
              const diff = todayWeekStart.getDate() - day + (day === 0 ? -6 : 1);
              todayWeekStart.setDate(diff);
              todayWeekStart.setHours(0, 0, 0, 0);
              
              if (currentWeekStart.getTime() !== todayWeekStart.getTime()) {
                calendarApi.gotoDate(today);
              }
            }
          }
        }}
      />
    </div>
  );
};

// Attach LocationSelector as a static property
Calendar.LocationSelector = function CalendarLocationSelector(props: any) {
  const { timetableData, ...otherProps } = props;
  const uniqueLocations = new Set(timetableData.map((entry: TimetableEntry) => entry.location));
  const locations = Array.from(uniqueLocations).sort();
  
  return <LocationSelector {...otherProps} locations={locations} />;
};

// Attach TeacherSelector as a static property
Calendar.TeacherSelector = function CalendarTeacherSelector(props: any) {
  const { timetableData, ...otherProps } = props;
  const uniqueTeachers = new Set(timetableData.map((entry: TimetableEntry) => entry.teacher));
  const teachers = Array.from(uniqueTeachers).sort();
  
  return <TeacherSelector {...otherProps} teachers={teachers} />;
};

// Attach RoomSelector as a static property
Calendar.RoomSelector = function CalendarRoomSelector(props: any) {
  const { timetableData, selectedLocation, ...otherProps } = props;
  
  if (selectedLocation === 'all') {
    return null;
  }
  
  const locationEntries = timetableData.filter((entry: TimetableEntry) => entry.location === selectedLocation);
  const uniqueRooms = new Set<string>();
  let hasUnassigned = false;
  
  locationEntries.forEach((entry: TimetableEntry) => {
    if (!entry.room || entry.room === '' || entry.roomId === 0) {
      hasUnassigned = true;
    } else {
      uniqueRooms.add(entry.room);
    }
  });
  
  const rooms = Array.from(uniqueRooms).sort();
  
  return <RoomSelector {...otherProps} rooms={rooms} showUnassigned={hasUnassigned} />;
};

export default Calendar;
